import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Canvas from "./Canvas";
import WidgetListPopUp from "./WidgetListPopUp";
import SimpleBackdrop from "../../../BackDrop/BackDrop";
import { useSelector } from "react-redux";
import { selectPageBuilderIsLoading } from "../../../../features/assetManagementSlice";
import { COMPONENT_TYPES } from "../utils/constants";

const PageBuilder = ({
  pageUrlComponents,
  header,
  footer,
  siteAllAvailableWidgets,
  currentpageElements,
  setisPublishOpen,
  setIsElementSaved,
  components,
  setComponents,
  pageDictionary,
  pageContent,
  activeInsertPosition,
  setActiveInsertPosition,
  setShowSidebar,
  showSidebar,
  setSelectedId,
  selectedId,
  siteBaseUrl,
}: any) => {
  const [openWidgetPopup, setOpenWidgetPopup] = useState(false);
  const [selectedWidgetType, setSelectedWidgetType] = useState("") as any; // or any default
  const [allWidgetsByType, setAllWidgetsByType] = useState([]);
  const [editingComponentId, setEditingComponentId] = useState<string | null>(
    null
  );
  const [deviceEdited, setDeviceEdited] = useState<any | undefined>();
  const [shareUidAvailable, setShareUidAvailable] = useState<any | undefined>();
  const [theme, setTheme] = useState<string | undefined>();
  const [widgetInsertOrder, setWidgetInsertOrder] = useState<number | null>(
    null
  );
  const selectPageBuilderIsLoader = useSelector(selectPageBuilderIsLoading);
  const widgetRecords = pageContent?.theme_v1?.records;
  const [isTextEditing, setIsTextEditing] = useState<string | null>(null);
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [unsavedText, setUnsavedText] = useState<string | null>(null);

  // Helper function to reset all widget editing state
  const resetWidgetEditingState = () => {
    setEditingComponentId(null);
    setDeviceEdited(null);
    setShareUidAvailable([]);
    setWidgetInsertOrder(null);
    setSelectedWidgetType("");
    setAllWidgetsByType([]);
  };

  const handleAddComponent = (component: any) => {
    const siblings = components.filter(
      (c: any) => c.parentId === component.parentId
    );
    const maxOrder =
      siblings.length > 0
        ? Math.max(...siblings.map((c: any) => c.order || 0))
        : 0;
    const componentWithOrder = { ...component, order: maxOrder + 1 };

    // add component
    setComponents((prev: any) =>
      [...prev, componentWithOrder].sort(
        (a: any, b: any) => (a.order || 0) - (b.order || 0)
      )
    );

    // focus/select new element
    setSelectedId(componentWithOrder.id);

    // if it's a text block, start editor immediately
    if (componentWithOrder.type === "text") {
      setIsTextEditing(componentWithOrder.id);
    }

    setIsElementSaved(true);
  };

  const handleDeleteComponent = (id: any) => {
    if (selectedId === id) {
      setSelectedId(null);
    }
    setComponents((prev: any) => prev.filter((c: any) => c.id !== id));
    setIsElementSaved(true);
  };

  const handleOpenWidgetPopup = () => {
    // Reset editing state when opening popup for new widget creation
    resetWidgetEditingState();
    setOpenWidgetPopup(true);
  };

  const handleUpdateComponent = (id: any, updates: any) => {
    setComponents((prev: any) =>
      prev.map((c: any) =>
        c.id === id
          ? {
              ...c,
              props: c.props ? { ...c.props, ...updates } : { ...updates },
            }
          : c
      )
    );
    setIsElementSaved(true);
  };

  const handleAddWidgets = (widgetUIDs: string[], devices: string[]) => {
    const selectedWidgets = siteAllAvailableWidgets.filter(
      (w: any) =>
        widgetUIDs.includes(w.shared_uid) && w.type === selectedWidgetType
    );

    // Step 2: Extract unique types and names
    const uniqueTypes = Array.from(
      new Set(selectedWidgets.map((w: any) => w.type))
    );
    const widgetNames = selectedWidgets
      .map((w: any) => w.widget_name)
      .join(", ");

    if (editingComponentId) {
      // EDITING MODE: Update existing widget component
      const existingComponent = components.find(
        (c: any) => c.id === editingComponentId
      );
      if (!existingComponent) return;

      const shortcode =
        widgetUIDs.length === 1
          ? `[render_widget shared_uid='${widgetUIDs[0]}' location='${devices[0]}']`
          : `[render_widget shared_uids='${widgetUIDs
              .map((uid) => `${uid}:1`)
              .join(",")}' location='${devices[0]}']`;

      const updatedComponent = {
        ...existingComponent,
        props: {
          ...existingComponent.props,
          text: shortcode,
          className: `internal-widget ${devices[0]}`,
          attributes: {
            widget_type: uniqueTypes.join(", "),
            widget_name: widgetNames,
          },
        },
      };

      // Update the existing component
      setComponents((prev: any) =>
        prev.map((c: any) =>
          c.id === editingComponentId ? updatedComponent : c
        )
      );

      // Reset all editing state after update
      resetWidgetEditingState();
    } else {
      // ADDING MODE: Create new widget components for each device
      // Use maxOrder instead of components.length to avoid order collisions after deletions.
      // Deleting a component reduces length but doesn't re-normalize existing orders,
      // so length-based assignment would collide with the order of the last remaining component.
      const maxOrder = components.length > 0
        ? Math.max(...components.map((c: any) => c.order || 0))
        : 0;

      const updatedOrNewComponents = devices.map((device, index) => {
        const shortcode =
          widgetUIDs.length === 1
            ? `[render_widget shared_uid='${widgetUIDs[0]}' location='${device}']`
            : `[render_widget shared_uids='${widgetUIDs
                .map((uid) => `${uid}:1`)
                .join(",")}' location='${device}']`;

        return {
          id: `${Date.now()}-${device}`,
          type: "widgets",
          parentId: null,
          order:
            // widgetInsertOrder is set when inserting at a specific position (e.g. between existing components).
            // Small fractional offsets (0.01) keep inserted items in-place without disrupting surrounding orders.
            // Falls back to appending after the current highest order when no insert position is specified.
            widgetInsertOrder !== null
              ? widgetInsertOrder + index * 0.01
              : maxOrder + index + 1,
          props: {
            text: shortcode,
            className: `internal-widget ${device}`,
            attributes: {
              widget_type: uniqueTypes.join(", "),
              widget_name: widgetNames,
            },
          },
        };
      });

      // Add new widgets
      setComponents((prev: any) => [...prev, ...updatedOrNewComponents]);
      const lastId =
        updatedOrNewComponents[updatedOrNewComponents.length - 1].id;
      setSelectedId(lastId);

      // Ensure we are not in text-edit mode
      setIsTextEditing(null);
    }
  };

  const handleEditWidget = (component: any) => {
    setEditingComponentId(component.id);

    const text = component.props.text;
    let sharedUIDs: string[] = [];

    // Try to extract from shortcode (if it's a new widget)
    const shortcodeMatch = text?.match(/shared_uids?='([^']+)'/);
    if (shortcodeMatch) {
      sharedUIDs = shortcodeMatch[1]
        .split(",")
        .map((entry: any) => entry.split(":")[0]);
    }

    // If it's not a shortcode (old widget), fallback to attribute.widget_name
    if (!sharedUIDs.length && component.props?.attributes?.widget_name) {
      const widgetNames = component.props.attributes.widget_name
        .split(",")
        .map((name: string) => name.trim());

      const widgetType = component.props.attributes?.widget_type;

      sharedUIDs = siteAllAvailableWidgets
        .filter(
          (w: any) =>
            widgetNames.includes(w.widget_name) && w.type === widgetType
        )
        .map((w: any) => w.shared_uid);
    }

    // Extract device from className - improved detection
    let device = "desktop"; // default
    const className = component.props.className || "";

    if (className.includes("mobile")) {
      device = "mobile";
    } else if (className.includes("tablet")) {
      device = "tablet";
    }

    // Also check the shortcode location attribute as backup
    const locationMatch = text?.match(/location='([^']+)'/);
    if (locationMatch && !className.includes(locationMatch[1])) {
      device = locationMatch[1];
    }

    const widgetType =
      component.props.attributes?.widget_type || "Comparison Table";

    setSelectedWidgetType(widgetType);
    setAllWidgetsByType(
      siteAllAvailableWidgets.filter((w: any) => w.type === widgetType)
    );
    setShareUidAvailable(sharedUIDs);
    setDeviceEdited(device);
    setOpenWidgetPopup(true);
  };

  const handleSelect = (id: any) => {
    // If editing and clicked on a different component
    if (isTextEditing && id !== isTextEditing) {
      setPendingSelectId(id); // Store the intended next selection
      setShowSaveDialog(true); // Show confirmation
      return; // Block selection until user decides
    }

    // Safe to select
    setSelectedId(id);
  };

  const handleMoveComponent = (id: any, direction: any) => {
    setComponents((prev: any) => {
      const component = prev.find((c: any) => c.id === id);
      if (!component) return prev;

      const siblings = prev
        .filter((c: any) => c.parentId === component.parentId)
        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

      const currentIndex = siblings.findIndex((c: any) => c.id === id);

      if (direction === "up" && currentIndex <= 0) return prev;
      if (direction === "down" && currentIndex >= siblings.length - 1)
        return prev;

      let newOrder;
      if (direction === "up") {
        const prevSibling = siblings[currentIndex - 1];
        newOrder = prevSibling.order - 0.5;
      } else {
        const nextSibling = siblings[currentIndex + 1];
        newOrder = nextSibling.order + 0.5;
      }

      const updatedComponents = prev.map((c: any) =>
        c.id === id ? { ...c, order: newOrder } : c
      );

      const componentsToNormalize = updatedComponents
        .filter((c: any) => c.parentId === component.parentId)
        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

      componentsToNormalize.forEach((comp: any, index: any) => {
        comp.order = index + 1;
      });

      return updatedComponents;
    });
    setIsElementSaved(true);
  };

  const handleCloseWidgetPopup = () => {
    setOpenWidgetPopup(false);
    // Reset all editing state when closing popup
    resetWidgetEditingState();
  };

  const insertComponent = (type: string, position: "above" | "below") => {
    if (!selectedId) return;

    const selected = components.find((c: any) => c.id === selectedId);
    if (!selected) return;

    const targetOrder = selected.order || 0;
    const newOrder =
      position === "above" ? targetOrder - 0.5 : targetOrder + 0.5;

    if (type === COMPONENT_TYPES.WIDGET) {
      // Reset editing state before opening popup for insertion
      resetWidgetEditingState();
      setWidgetInsertOrder(newOrder);
      setOpenWidgetPopup(true);
      return;
    }

    const newComponent = {
      id: Date.now().toString(),
      type,
      parentId: null,
      order: newOrder,
      props: {
        text: "New text block",
        fontSize: 16,
        textAlign: "left",
        color: "#000000",
      },
    };

    setComponents((prev: any) =>
      [...prev, newComponent].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    );
    // select and if text -> open editor
    setSelectedId(newComponent.id);
    if (type === COMPONENT_TYPES.TEXT) {
      setIsTextEditing(newComponent.id);
    }
  };

  useEffect(() => {
    const data = JSON.parse(pageDictionary); // Parse the page dictionary string into a JavaScript object
    const themeValue = data?.settings?.theme; // Extract the theme value from the parsed data
    setTheme(themeValue); // Set the extracted theme value into the state variable 'theme'
  }, [pageDictionary]);

  const widgetListPopUpProps: any = {
    allWidgetsByType,
    statusClicked: "dragged",
    deviceEdited: deviceEdited,
    handelCLickEditWidget: () => {},
    setOpen: setOpenWidgetPopup,
    setisPublishOpen,
    setIsElementSaved,
    selectedWidgettype: selectedWidgetType,
    shareUidAvailable: shareUidAvailable,
    onAddWidgets: handleAddWidgets,
    handleCloseWidgetPopup,
    siteAllAvailableWidgets,
    setSelectedWidgetType,
    setAllWidgetsByType,
    widgetRecords,
    selectedWidgetType,
    setOpenWidgetPopup,
  };

  const handleInsertAbove = (type: string) => insertComponent(type, "above");
  const handleInsertBelow = (type: string) => insertComponent(type, "below");

  const confirmSwitchComponent = () => {
    if (unsavedText && isTextEditing) {
      handleUpdateComponent(isTextEditing, { text: unsavedText });
    }
    if (pendingSelectId) {
      setSelectedId(pendingSelectId);
    }
    setIsTextEditing(null); // Exit edit mode
    setPendingSelectId(null);
    setShowSaveDialog(false);
    setUnsavedText(null); // Clear unsaved text
  };

  const cancelSwitchComponent = () => {
    if (pendingSelectId) {
      setSelectedId(pendingSelectId); // Go to the new component
    }
    setIsTextEditing(null); // Exit editing mode (discard changes)
    setPendingSelectId(null);
    setShowSaveDialog(false);
  };

  return (
    <>
      {header ? (
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            height: "calc(100vh - 64px - 150px)",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <Paper>
            <Stack direction="row">
              {(showSidebar || selectedId) && (
                <Sidebar
                  components={components}
                  selectedId={selectedId}
                  onAddComponent={handleAddComponent}
                  onDeleteComponent={handleDeleteComponent}
                  onUpdateComponent={handleUpdateComponent}
                  onMoveComponent={handleMoveComponent}
                  currentpageElements={currentpageElements}
                  onOpenWidgetPopup={handleOpenWidgetPopup}
                  handleAddWidgets={handleAddWidgets}
                  onInsertAbove={handleInsertAbove}
                  onInsertBelow={handleInsertBelow}
                  onCloseSidebar={() => {
                    setShowSidebar(false);
                    setSelectedId(null); // Deselect component on cancel
                  }}
                  onEditWidget={(component: any) => {
                    if (typeof handleEditWidget === "function") {
                      handleEditWidget(component);
                    }
                  }}
                  setIsTextEditing={setIsTextEditing}
                  setActiveInsertPosition={setActiveInsertPosition}
                  activeInsertPosition={activeInsertPosition}
                  isTextEditing={isTextEditing}
                />
              )}

              {selectPageBuilderIsLoader && <SimpleBackdrop />}
              <Canvas
                components={components}
                selectedId={selectedId}
                onSelect={handleSelect}
                onUpdate={handleUpdateComponent}
                pageUrlComponents={pageUrlComponents}
                header={header}
                footer={footer}
                siteAllAvailableWidgets={siteAllAvailableWidgets}
                currentpageElements={currentpageElements}
                onEditWidget={handleEditWidget}
                setIsElementSaved={setIsElementSaved}
                theme={theme}
                pageDictionary={pageDictionary}
                onAddComponent={handleAddComponent}
                onOpenWidgetPopup={handleOpenWidgetPopup}
                setShowSidebar={setShowSidebar}
                setIsTextEditing={setIsTextEditing}
                isTextEditing={isTextEditing}
                setUnsavedText={setUnsavedText}
                siteBaseUrl={siteBaseUrl}
              />
            </Stack>
          </Paper>
          <Dialog
            onClose={(_, reason) => {
              if (reason !== "backdropClick") {
                setOpenWidgetPopup(false);
              }
            }}
            open={openWidgetPopup}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Choose a Widget</DialogTitle>
            <DialogContent>
              <WidgetListPopUp {...widgetListPopUpProps} />
            </DialogContent>
          </Dialog>
        </Container>
      ) : (
        <SimpleBackdrop />
      )}
      <Dialog open={showSaveDialog} onClose={() => {}}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>Do you want to save changes before switching?</Typography>
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" p={2} gap={1}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={cancelSwitchComponent}
          >
            No
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={confirmSwitchComponent}
          >
            Yes
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default PageBuilder;
